<template>
    <div>
        <div class="filters-section">
            <h3 class="filters-title">
                <span>
                    <q-icon name="tune" class="filter-icon" />
                    Filters
                </span>
            </h3>

            <q-expansion-item class="expansion-panel" label="Group Filters" switch-toggle-side
                expand-icon="keyboard_arrow_down" expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Database</label>
                            <q-select clearable outlined label-color="black" bg-color="white" multiple
                                v-model="selectedDatabases" use-chips :options="Object.keys(databaseList)" />
                            <q-tooltip class="text-body2">
                                This will display a list of current databases from paua
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Group Types</label>
                            <q-select class="client-select-input" clearable outlined label-color="black"
                                bg-color="white" multiple v-model="selectedGroupTypes" use-chips
                                :options="groupTypesList" />
                            <q-tooltip class="text-body2">
                                This will display a list of current group types available within Paua
                            </q-tooltip>
                        </div>
                    </div>
                </div>
            </q-expansion-item>

            <q-expansion-item class="expansion-panel" label="Date Filters" switch-toggle-side
                expand-icon="keyboard_arrow_down" expanded-icon="keyboard_arrow_up" header-class="expansion-header">
                <div class="expansion-content">
                    <div class="expansion-inner">
                        <div class="filter-input-group">
                            <label class="form-label">Start Date From</label>
                            <q-input class="form-control-quasar" outlined label-color="black" v-model="startDateFrom"
                                mask="####-##-##" :placeholder="dateFormat" clearable
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="startDateFrom" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                If no filter from date is provided, will default to 90 days from current date.
                            </q-tooltip>
                        </div>

                        <div class="filter-input-group">
                            <label class="form-label">Start Date To</label>
                            <q-input class="form-control-quasar" outlined label-color="black" v-model="startDateTo"
                                mask="####-##-##" :placeholder="dateFormat" clearable
                                :rules="[(val) => (dayjs(val, dateFormat, true).isValid() || val == '') || 'Please enter a valid date']">
                                <template v-slot:append>
                                    <q-icon name="event" class="cursor-pointer">
                                        <q-popup-proxy transition-show="scale" transition-hide="scale">
                                            <q-date v-model="startDateTo" :mask="dateFormat" />
                                        </q-popup-proxy>
                                    </q-icon>
                                </template>
                            </q-input>
                            <q-tooltip class="text-body2">
                                If no filter to date is provided, will default to the current date.
                            </q-tooltip>
                        </div>
                    </div>
                </div>
            </q-expansion-item>
            
            <div class="filter-actions">
                <q-btn class="btn btn-secondary" @click="resetFilters" outline color="grey-7" icon="refresh"
                    label="Reset" no-caps />
                <q-btn class="btn btn-primary" @click="applyFilters" color="secondary" icon="check" label="Apply Filters"
                    no-caps />
            </div>
        </div>

        <div style="margin: 1rem;">
            <q-table class="client-table" v-if="tableLoaded" :loading="retrievingTableData"
                :rows="filteredTableDataRows" :columns="tableDataColumns" :rows-per-page-options="[25]" :filter="filter"
                @row-click="onRowClick">

                <template v-slot:top-left>
                    <div class="users-title view-user-title">
                        <q-input outlined dense debounce="300" v-model="filter" placeholder="Search Groups">
                            <template v-slot:append>
                                <q-icon name="close" @click="filter = ''" class="cursor-pointer" />
                                <q-icon name="search" />
                            </template>
                        </q-input>

                        <q-toggle class="client-select-input" label="Active Groups Only" v-model="activeGroup" />
                    </div>
                </template>

                <template v-slot:body-cell="props">
                    <q-td :props="props">
                        <span v-if="props.value && props.col.name.includes('date')">
                            {{ props.value.split('T')[0] }}
                        </span>
                        <span v-else>
                            {{ props.value }}
                        </span>
                    </q-td>
                </template>

            </q-table>

            <q-linear-progress v-else indeterminate />
        </div>
        
        <GroupInformationComponent v-if="showGroupInformation" :groupRow="groupRow" @close="handleClose" />
    </div>
</template>

<script>
import api from "../../services/api/api"
import notify from '@/services/util/notify';
import dayjs from "@/services/util/dayjs";
import GroupInformationComponent from "./IndividualPopupComponents/GroupInformationComponent.vue";

export default ({
    name: "ClientGroup",

    components: { GroupInformationComponent },

    props: {
        databaseList: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            groupTypesList: [],
            selectedDatabases: null,
            selectedGroupTypes: null,
            tableLoaded: false,
            retrievingTableData: false,
            groupRow: null,
            showGroupInformation: false,
            tableDataRows: [],
            filteredTableDataRows: [],
            tableDataColumns: [],
            filter: null,
            query: '',
            dayjs: dayjs,
            dateFormat: 'YYYY-MM-DD',
            startDateFrom: "",
            startDateTo: "",
            activeGroup: false,
        }
    },

    created() {
        this.retrieveGroupTypes();
        this.retrieveTableData();
    },

    watch: {
        async query() {
            await this.retrieveTableData();
        },

        activeGroup() {
            this.applyActiveGroupToggle();
        }
    },

    methods: {
        async retrieveGroupTypes() {
            try {
                const res = await api.groups.getGroupTypes();
                this.groupTypesList = res.data['group_type'];
            } catch (error) {
                console.error(error.message);
                notify.error("Error retrieving group types lists", "top");
            }
        },

        async retrieveTableData() {
            this.retrievingTableData = true;

            try {
                const res = await api.client.getClientTableData("groups", this.query);
                this.tableDataRows = res.data.tableRows;
                this.filteredTableDataRows = this.tableDataRows;
                this.tableDataColumns = res.data.tableColumns;
                this.tableLoaded = true;
            } catch (error) {
                notify.error(error.message, 'top');
            } finally {
                this.retrievingTableData = false;
            }
        },

        applyFilters() {
            const filters = {};

            this.checkFilters();

            if (this.selectedGroupTypes) { 
                filters.group_type = this.selectedGroupTypes; 
            }
            if (this.selectedDatabases) {
                filters.source = this.selectedDatabases.map((database) => this.databaseList[database]);
            }
            if (this.startDateFrom) { 
                filters.groupDateFrom = this.startDateFrom; 
            }
            if (this.startDateTo) { 
                filters.groupDateTo = this.startDateTo; 
            }

            this.query = "?filters=" + encodeURIComponent(JSON.stringify(filters));
        },

        checkFilters() {
            this.selectedGroupTypes = (this.selectedGroupTypes !== null && this.selectedGroupTypes.length > 0) ? this.selectedGroupTypes : null;
            this.selectedDatabases = (this.selectedDatabases !== null && this.selectedDatabases.length > 0) ? this.selectedDatabases : null;
        },

        resetFilters() {
            this.selectedGroupTypes = null;
            this.selectedDatabases = null;
            this.startDateFrom = "";
            this.startDateTo = "";
            this.query = '';
        },

        onRowClick(evt, row) {
            this.groupRow = row;
            this.showGroupInformation = true;
        },

        handleClose() {
            this.showGroupInformation = false;
        },

        applyActiveGroupToggle() {
            if (this.activeGroup) {
                this.filteredTableDataRows = this.tableDataRows.filter((item) => item.status == 'Active');
            } else {
                this.filteredTableDataRows = this.tableDataRows;
            }
        }
    }
})
</script>

<style>
@import '../../assets/styles/clientDemographics.css';
</style>
